import { cn } from '@repo/ui/lib/utils'
import { cva, VariantProps } from 'class-variance-authority'

// Atom for a text element (p by default) with a weight, size, alignment, tone, and maxLines

export type TextProps = {
  as?: React.ElementType
} & React.ComponentPropsWithoutRef<React.ElementType> &
  VariantProps<typeof textVariants>

const textVariants = cva('tracking-normal', {
  variants: {
    weight: {
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
    },
    size: {
      xs: 'text-xs',
      default: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
    },
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    },
    tone: {
      default: 'text-foreground',
      muted: 'text-muted-foreground',
      critical: 'text-critical-text',
      'critical-dark': 'text-critical-text-dark',
      success: 'text-success-text',
      'success-dark': 'text-success-text-dark',
      warning: 'text-warning-text',
      'warning-dark': 'text-warning-text-dark',
      info: 'text-info-text',
      'info-dark': 'text-info-text-dark',
    },
    leading: {
      none: 'leading-none',
      tight: 'leading-tight',
      normal: 'leading-normal',
    },
    maxLines: {
      one: 'line-clamp-1 text-ellipsis',
      two: 'line-clamp-2 text-ellipsis',
      three: 'line-clamp-3 text-ellipsis',
    },
    monospace: {
      true: 'font-mono',
    },
  },
  defaultVariants: {
    weight: 'normal',
    size: 'default',
    align: 'left',
    leading: 'normal',
    monospace: false,
  },
})

export function Text({ as: Component = 'p', ...props }: TextProps) {
  const { weight, size, align, tone, maxLines, leading, monospace, className, ...rest } = props

  return (
    <Component
      className={cn(
        textVariants({ weight, size, align, tone, maxLines, leading, monospace }),
        className,
      )}
      {...rest}
    />
  )
}
